import React, {useMemo} from 'react';
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CCOLinkable from "../../fundamentals/CCOLinkable";
import C036SearchField from "../../content/C036SearchField/C036SearchField";
import CCOContainer from "../../fundamentals/CCOContainer";
import {usePageContext} from "../../../context/PageContext";
import {SkipLinks} from "../../fundamentals/SkipLinks";
import {CmCollectionType, CmLinkableType, PageGridPlacementType} from "../../types";
import EasyLangToggleButton from "../navigation/EasyLangToggleButton";
import {useNavigationTracking} from 'tracking/hooks/useNavigationTracking';

export interface Props {
    empty?: boolean
    header?: PageGridPlacementType
    onItemClick?: any
    topNaviStyle?: any
    page?: any
    navigation?: any
}


const defaultTopNaviStyle = {
    height: 52,
    position: "sticky",
    top: 0,
    zIndex: 1200,
    display: {
        xs: "none",
        lg: "flex"
    },
    justifyContent: "space-between",
    width: '100%',
    flexGrow: 1,
    alignItems: "center",
    backgroundColor: "#ffffff"
}

const topNaviHoverStyle = {
    "&:hover": {
        color: "primary.main",
        background: "none",
        textDecoration: "underline  2px",
        textDecorationColor: "primary.main",
        textUnderlineOffset: "4px",
    },
}

const searchFieldStyle = {style: {fontSize: 'var(--font-size-menu-item)'}}

const renderLink = (i: any, index: number, onItemClick?: any, track?: (label: string) => void) => {
    const key = "key" + index;

    const handleClick = (e: React.MouseEvent) => {
        track?.(i?.teaserTitle);
        onItemClick?.(e);

        // Force a full page reload when navigating to the homepage to ensure the chatbot is loaded correctly.
        const url = i?.teaserTarget?.selfUrl || i?.selfUrl;
        if (url === "/portal" || url === "/" || url === "https://www.sos-kinderdorf.de" || url === "https://www.sos-kinderdorf.de/") {
            e.preventDefault();
            window.location.href = url;
        }
    };

    return (
        <React.Fragment key={key}>
            {index === 0 ? null : <Divider orientation="vertical" flexItem sx={{marginX: 3}}/>}
            <Grid>
                <CCOLinkable key={index} target={i} onClick={handleClick}>
                    <Typography sx={topNaviHoverStyle} variant="navigationItemDense">
                        {i.teaserTitle}
                    </Typography>
                </CCOLinkable>
            </Grid>
        </React.Fragment>
    )
};

const TopNavi = (props: Props) => {
    const topNaviStyle = props.topNaviStyle ?? defaultTopNaviStyle;

    const items = useMemo(() => {
        const topNavi = props.header?.items?.find(
            (cmCol: CmLinkableType) => cmCol.viewtype === "asTopNavi") as CmCollectionType;
        return topNavi?.items ?? [];
    }, [props.header]);

    const {trackMetaNavigation} = useNavigationTracking();

    const {hideTopNavi} = usePageContext()
    if (hideTopNavi) {
        return (
            <SkipLinks
                isWithoutNavi={props.empty}
                isWithoutTopNavi
            />
        );
    }

    return (
        <React.Fragment>
            <Container id="topnavi" maxWidth={false} data-object-name="Top Navigation"
                       data-cypress="topNavi"
                       sx={topNaviStyle}>
                <CCOContainer width="full">
                    <SkipLinks isWithoutNavi={props.empty}/>
                    <Box
                        data-cypress="topNaviSearchField"
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%"
                        }}>
                        <Grid
                            container
                            sx={{
                                justifyContent: "flex-start",
                                gap: 3,
                                mx: 2,
                                flex: 1
                            }}>
                            {!props.empty ? items.map(
                                (item, index) => renderLink(item, index, props.onItemClick,
                                    trackMetaNavigation)) : null}
                        </Grid>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2
                            }}>
                            {!props.empty ? <Box
                                sx={{
                                    width: "280px",
                                    minWidth: "280px",
                                    display: "flex",
                                    justifyContent: "flex-end"
                                }}>
                                <EasyLangToggleButton navigation={props.navigation}/>
                            </Box> : null}
                            {!props.empty ?
                                <Box sx={{width: "210px", minWidth: "210px"}}>
                                    <C036SearchField dataCypress='naviSearchField'
                                                     customInputProps={searchFieldStyle}/>
                                </Box> : null}
                        </Box>
                    </Box>
                </CCOContainer>
            </Container>
            <Box
                sx={{
                    display: props.topNaviStyle ? "none" : {xs: "flex", lg: "none"},
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 6
                }}>
                <Grid container sx={{
                    justifyContent: "flex-start"
                }}>
                    {!props.empty ?
                        items.map((item, index) => renderLink(item, index, props.onItemClick,
                            trackMetaNavigation)) :
                        null}
                </Grid>
            </Box>
        </React.Fragment>
    );
};

export default React.memo(TopNavi);
